<template>
  <Teleport to="body">
    <div class="toast-provider">
      <TransitionGroup name="toast">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="toast-provider__item"
          :class="{
            'toast-provider__item--success': toast.type === 'success',
            'toast-provider__item--error': toast.type === 'error',
          }"
        >
          <div class="toast-provider__content">
            <CheckCircleIcon v-if="toast.type === 'success'" class="toast-provider__icon" />
            <XCircleIcon v-else class="toast-provider__icon" />
            <p class="toast-provider__message">
              {{ toast.message }}
            </p>
            <button class="toast-provider__dismiss" @click="dismissToast(toast.id)">
              <XMarkIcon class="h-4 w-4" />
            </button>
          </div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { CheckCircleIcon, XCircleIcon, XMarkIcon } from "@heroicons/vue/24/outline";

import { useToast } from "@/composables/useToast";

const { dismissToast, toasts } = useToast();
</script>

<style scoped>
.toast-provider {
  position: fixed;
  top: 1rem;
  right: 1rem;
  /* Must sit above every modal shell — legacy /components/ui/Modal.vue uses
     z-index: 100000, so toasts need one more. */
  z-index: 100001;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  pointer-events: none;
}

.toast-provider__item {
  padding: 1rem;
  border-radius: 0.5rem;
  box-shadow:
    0 10px 15px -3px rgb(0 0 0 / 0.1),
    0 4px 6px -4px rgb(0 0 0 / 0.1);
  max-width: 28rem;
  pointer-events: auto;
}

.toast-provider__item--success {
  background-color: var(--color-green-50);
  border: 1px solid var(--color-green-200);
}

.dark .toast-provider__item--success {
  background-color: var(--color-green-900);
  border-color: var(--color-green-800);
}

.toast-provider__item--error {
  background-color: var(--color-red-50);
  border: 1px solid var(--color-red-200);
}

.dark .toast-provider__item--error {
  background-color: var(--color-red-900);
  border-color: var(--color-red-800);
}

.toast-provider__content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.toast-provider__icon {
  height: 1.25rem;
  width: 1.25rem;
  flex-shrink: 0;
}

.toast-provider__item--success .toast-provider__icon {
  color: var(--color-green-500);
}

.toast-provider__item--error .toast-provider__icon {
  color: var(--color-red-500);
}

.toast-provider__message {
  font-size: 0.875rem;
  font-weight: 500;
}

.toast-provider__item--success .toast-provider__message {
  color: var(--color-green-800);
}

.dark .toast-provider__item--success .toast-provider__message {
  color: var(--color-green-200);
}

.toast-provider__item--error .toast-provider__message {
  color: var(--color-red-800);
}

.dark .toast-provider__item--error .toast-provider__message {
  color: var(--color-red-200);
}

.toast-provider__dismiss {
  margin-left: auto;
  flex-shrink: 0;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.15s;
}

.toast-provider__dismiss:hover {
  opacity: 1;
}

.toast-provider__item--success .toast-provider__dismiss {
  color: var(--color-green-500);
}

.toast-provider__item--error .toast-provider__dismiss {
  color: var(--color-red-500);
}

/* TransitionGroup animations */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.toast-move {
  transition: transform 0.3s ease;
}
</style>
